/**
 * AppShell — the canonical wrapper for every Wall-E Workbench app.
 *
 * Encapsulates everything an app would otherwise hand-roll on every render:
 *   - DensityProvider (Compact / Cozy / Comfortable)
 *   - ErrorBoundary (single source of crash UI)
 *   - AppLayout chrome (title / subtitle / actions / scrollable body)
 *   - Tabs (declarative — apps describe tabs as data, not JSX)
 *   - DetailDrawer state (apps open via `useDetailDrawer().open(item)`)
 *   - Optional FreshnessChip header slot (drive from `freshness` prop or `view`)
 *   - Optional dismissible banner (info / warning / error / success)
 *   - Optional NowBar slot (above tabs, below banner)
 *   - Built-in keyboard shortcuts (Esc closes drawer)
 *
 * Industry term: this is the "App Shell" / "Scaffold" pattern (Flutter
 * Scaffold, Next.js Layout, MUI AppFrame, Salesforce Page Template).
 *
 * Apps using AppShell are 5-line shells plus tab implementations — change
 * AppShell once and every app inherits the fix.
 *
 * @example
 *   <AppShell
 *     title="Station Diagnostics"
 *     subtitle="OCPP commands and configuration"
 *     freshness={{ state: 'live', updatedAt: lastSync }}
 *     tabs={[
 *       { value: 'overview', label: 'Overview', render: () => <OverviewTab/> },
 *       { value: 'commands', label: 'Commands', render: () => <CommandsTab/> },
 *     ]}
 *     detailDrawer={{
 *       renderTitle: (s) => s.name,
 *       renderSubtitle: (s) => <MonoText tone="brand">{s.id}</MonoText>,
 *       render: (s) => <StationDetailContent station={s}/>,
 *     }}
 *   />
 */
import {
  Component,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ErrorInfo,
  type ReactElement,
  type ReactNode,
} from 'react';
import { DetailDrawerContext, type DetailDrawerContextValue } from './detailDrawerContext';
import {
  Body1,
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Tab,
  TabList,
  Title3,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import {
  DismissRegular,
  ErrorCircleFilled,
  PanelLeftContractRegular,
  PanelLeftExpandRegular,
} from '@fluentui/react-icons';
import { AppLayout } from './AppLayout';
import { DensityProvider } from './DensityProvider';
import type { Density } from './densityContext';
import { DetailDrawer, type DetailDrawerSize, type DetailDrawerVariant } from './DetailDrawer';
import { FreshnessChip, type FreshnessState } from './FreshnessChip';
import { useLocale } from '../i18n/useLocale';

/* ──────────────────────────────────────────────────────────────────────
 * Types
 * ────────────────────────────────────────────────────────────────────── */

export interface AppShellTab {
  value: string;
  label: string;
  icon?: ReactElement;
  /** Render the tab body. Called only when tab is active. */
  render: () => ReactNode;
}

export interface AppShellBanner {
  intent: 'info' | 'warning' | 'error' | 'success';
  title: string;
  body: ReactNode;
  /** When true, banner shows a dismiss button. Defaults to true. */
  dismissible?: boolean;
}

export interface AppShellDetailDrawerConfig<T = unknown> {
  /**
   * `inline` (default) squeezes the tab body beside the drawer; `overlay`
   * floats above it with a backdrop + light dismiss. Use `overlay` for
   * drill-downs that must not reflow the table behind them.
   */
  variant?: DetailDrawerVariant;
  /** Width in pixels (inline variant). Defaults to 360. */
  width?: number;
  /** Fluent width scale for the overlay variant. Defaults to 'medium'. */
  size?: DetailDrawerSize;
  /** Side of the screen. Defaults to 'end'. */
  position?: 'start' | 'end';
  /** Title rendered in the drawer header. */
  renderTitle: (item: T) => ReactNode;
  /** Subtitle below the title. */
  renderSubtitle?: (item: T) => ReactNode;
  /** Body content. */
  render: (item: T) => ReactNode;
  /** Optional sticky footer (action buttons). */
  renderFooter?: (item: T) => ReactNode;
}

export interface AppShellFreshness {
  state: FreshnessState;
  updatedAt?: Date | string;
  showAge?: boolean;
}

export interface AppShellProps {
  /** Title in the AppLayout header. */
  title: string;
  /** Optional subtitle (under the title). */
  subtitle?: ReactNode;
  /** Optional actions in the header right slot. */
  actions?: ReactNode;
  /** Density mode. Defaults to "cozy". Persisted to localStorage. */
  density?: Density;
  /** Optional freshness chip rendered alongside header actions. */
  freshness?: AppShellFreshness;
  /** Optional dismissible banner shown below the header. */
  banner?: AppShellBanner | ReactNode;
  /** Optional NowBar slot (above tabs, below banner) — typically a Severity stripe. */
  nowBar?: ReactNode;
  /** Declarative tabs. When provided, AppShell renders the tab list and routes content. */
  tabs?: AppShellTab[];
  /** Default selected tab. Defaults to first tab's value. */
  defaultTab?: string;
  /** Controlled tab value. Pair with `onTabChange`. */
  activeTab?: string;
  onTabChange?: (value: string) => void;
  /** Optional row-driven detail drawer. Apps use `useDetailDrawer()` to open. */
  detailDrawer?: AppShellDetailDrawerConfig<unknown>;
  /**
   * Controls AppShell body padding for workspace-style apps.
   * `standard` keeps the canonical padded tab body. `fullBleed` lets
   * cockpit/map/table tabs own every pixel inside the Workbench window.
   */
  contentMode?: 'standard' | 'fullBleed';
  /**
   * Optional always-visible left rail rendered alongside the tab body.
   * Use for inspector-style apps (Station Diagnostics, Connector Detail)
   * where a single entity's summary + actions need to stay on screen
   * while the user moves between tabs.
   *
   * Collapsible by the user via a chevron in the rail header. Width
   * defaults to 280 px when expanded; the collapse state persists per
   * app to ``localStorage`` under the ``app.shell.rail.collapsed`` key.
   */
  leftRail?: ReactNode;
  /** Width of the left rail in pixels when expanded. Defaults to 280. */
  leftRailWidth?: number;
  /**
   * Stable id for the rail's collapse state in ``localStorage``. Apps
   * pass their manifest id so each app remembers its own preference.
   * When omitted, falls back to the title-based key.
   */
  leftRailStorageKey?: string;
  /** Body fallback when `tabs` is not provided. */
  children?: ReactNode;
  /** Custom error boundary fallback. */
  errorFallback?: (error: Error, reset: () => void) => ReactNode;
}

/* ──────────────────────────────────────────────────────────────────────
 * DetailDrawer context lives in ./detailDrawerContext (react-refresh:
 * this file must export only components). AppShell renders the provider.
 * ────────────────────────────────────────────────────────────────────── */

/* ──────────────────────────────────────────────────────────────────────
 * Error boundary
 * ────────────────────────────────────────────────────────────────────── */

interface ErrorBoundaryProps {
  fallback: (error: Error, reset: () => void) => ReactNode;
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

class AppShellErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    /* Log to console; production reporting can wrap this later. */
    /* eslint-disable-next-line no-console */
    console.error('[AppShell] Uncaught render error:', error, info);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      return this.props.fallback(this.state.error, this.reset);
    }
    return this.props.children;
  }
}

/* ──────────────────────────────────────────────────────────────────────
 * Default error fallback UI
 * ────────────────────────────────────────────────────────────────────── */

const useErrorFallbackStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalXXL,
    color: tokens.colorPaletteRedForeground1,
  },
  message: {
    color: tokens.colorNeutralForeground2,
    maxWidth: '60ch',
  },
});

function DefaultErrorFallback({ error, reset }: { error: Error; reset: () => void }) {
  const styles = useErrorFallbackStyles();
  const { t } = useLocale();
  return (
    <div className={styles.root}>
      <ErrorCircleFilled fontSize={28} />
      <Title3>{t('shell.error.generic')}</Title3>
      <Body1 className={styles.message}>{error.message || String(error)}</Body1>
      <Button appearance="primary" onClick={reset}>{t('shell.error.tryAgain')}</Button>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * AppShell — main component
 * ────────────────────────────────────────────────────────────────────── */

const useStyles = makeStyles({
  banner: {
    flexShrink: 0,
  },
  tabList: {
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingLeft: tokens.spacingHorizontalL,
    flexShrink: 0,
    overflowX: 'auto',
  },
  tabBody: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    padding: tokens.spacingHorizontalL,
  },
  tabBodyFullBleed: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    padding: 0,
  },
  tabContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  tabContentFullBleed: {
    flex: 1,
    height: '100%',
    minHeight: 0,
    gap: 0,
  },
  fallbackBody: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    padding: tokens.spacingHorizontalL,
  },
  fallbackBodyFullBleed: {
    // Mirror tabBodyFullBleed / tabContent: a no-tabs fullBleed app's children
    // get a real flex-column context, so a `flex:1` scroll region inside gets
    // a bounded height instead of overflowing and clipping. (This is the
    // durable root-cause fix for the v0.70.1 Case Management scroll-clip class
    // — audited safe: all fullBleed-no-tabs apps render a single wrapper child,
    // which simply stretches in the column.)
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    padding: 0,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  /* Horizontal track for the body + the optional inline DetailDrawer.
   * The InlineDrawer is a flex-ROW citizen (flexShrink: 0, borderLeft) —
   * as a direct child of AppLayout's column it lands BELOW the body and
   * starves it to height 0. This wrapper is the row it expects. */
  bodyRow: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden',
  },
  /* Left rail layout — wraps tabs/fallback body and the optional rail. */
  body: {
    flex: 1,
    height: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden',
  },
  rail: {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
    minHeight: 0,
    overflow: 'hidden',
    transition: 'width 160ms ease',
  },
  railHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalXS}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    flexShrink: 0,
  },
  railContent: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
  },
  railCollapsed: {
    width: '32px',
  },
  bodyMain: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
  },
});

export function AppShell({
  title,
  subtitle,
  actions,
  density = 'cozy',
  freshness,
  banner,
  nowBar,
  tabs,
  defaultTab,
  activeTab: controlledTab,
  onTabChange,
  detailDrawer,
  contentMode = 'standard',
  leftRail,
  leftRailWidth = 280,
  leftRailStorageKey,
  children,
  errorFallback,
}: AppShellProps) {
  const styles = useStyles();
  const { t } = useLocale();
  const isTabsControlled = controlledTab !== undefined;

  /* Left rail collapse state — persisted per-app to localStorage so the
     user's preference survives reload. Falls back to the title when no
     stable storage key is supplied. */
  const railStorageKey = `app.shell.rail.collapsed:${leftRailStorageKey ?? title}`;
  const [railCollapsed, setRailCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(railStorageKey) === '1';
    } catch {
      return false;
    }
  });
  const toggleRail = useCallback(() => {
    setRailCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(railStorageKey, next ? '1' : '0');
      } catch { /* no-op — private mode / storage disabled */ }
      return next;
    });
  }, [railStorageKey]);

  /* Tabs */
  const initialTab = defaultTab ?? tabs?.[0]?.value ?? '';
  const [internalTab, setInternalTab] = useState<string>(initialTab);
  const currentTab = isTabsControlled ? controlledTab! : internalTab;
  const setCurrentTab = useCallback((v: string) => {
    if (!isTabsControlled) setInternalTab(v);
    onTabChange?.(v);
  }, [isTabsControlled, onTabChange]);

  /* Banner */
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const bannerNode = useMemo(() => {
    if (!banner || bannerDismissed) return null;
    /* Caller passed a ReactNode banner — render as-is. */
    if (
      typeof banner !== 'object' ||
      banner === null ||
      !('intent' in (banner as object))
    ) {
      return banner as ReactNode;
    }
    const b = banner as AppShellBanner;
    const dismissible = b.dismissible !== false;
    return (
      <MessageBar intent={b.intent} className={styles.banner}>
        <MessageBarBody>
          <MessageBarTitle>{b.title}</MessageBarTitle>
          {b.body}
        </MessageBarBody>
        {dismissible ? (
          <MessageBarActions
            containerAction={
              <Button
                appearance="transparent"
                size="small"
                icon={<DismissRegular />}
                onClick={() => setBannerDismissed(true)}
                aria-label={t('shell.banner.dismiss')}
              />
            }
          />
        ) : null}
      </MessageBar>
    );
  }, [banner, bannerDismissed, styles.banner, t]);

  /* DetailDrawer state */
  const [drawerItem, setDrawerItem] = useState<unknown>(null);
  const drawerOpen = drawerItem !== null;
  const openDrawer = useCallback((item: unknown) => setDrawerItem(item), []);
  const closeDrawer = useCallback(() => setDrawerItem(null), []);

  const drawerCtx = useMemo<DetailDrawerContextValue>(
    () => ({ current: drawerItem, open: openDrawer, close: closeDrawer }),
    [drawerItem, openDrawer, closeDrawer],
  );

  /* Esc closes drawer */
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDrawer();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen, closeDrawer]);

  /* Composite header actions: app's actions + freshness chip when present */
  const headerActionsNode = useMemo(() => {
    if (!freshness && !actions) return undefined;
    return (
      <div className={styles.headerActions}>
        {freshness ? (
          <FreshnessChip
            state={freshness.state}
            updatedAt={freshness.updatedAt}
            showAge={freshness.showAge}
          />
        ) : null}
        {actions}
      </div>
    );
  }, [freshness, actions, styles.headerActions]);

  const fallback = errorFallback ?? ((e: Error, reset: () => void) => (
    <DefaultErrorFallback error={e} reset={reset} />
  ));

  return (
    <DensityProvider defaultDensity={density}>
      <DetailDrawerContext.Provider value={drawerCtx}>
        <AppShellErrorBoundary fallback={fallback}>
          <AppLayout
            title={title}
            subtitle={typeof subtitle === 'string' ? subtitle : undefined}
            actions={headerActionsNode}
            noPadding
          >
            {/* Subtitle as ReactNode is rendered via banner-area for richer subtitles */}
            {typeof subtitle !== 'string' && subtitle ? subtitle : null}

            {bannerNode}

            {nowBar ? <div className={styles.banner}>{nowBar}</div> : null}

            <div className={styles.bodyRow}>
            <div className={styles.body}>
              {leftRail ? (
                <aside
                  className={mergeClasses(styles.rail, railCollapsed && styles.railCollapsed)}
                  style={{ width: railCollapsed ? undefined : leftRailWidth }}
                  aria-label={t('shell.rail.label')}
                >
                  <div className={styles.railHeader}>
                    <Tooltip
                      content={railCollapsed ? t('shell.rail.expand') : t('shell.rail.collapse')}
                      relationship="label"
                    >
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={railCollapsed ? <PanelLeftExpandRegular /> : <PanelLeftContractRegular />}
                        onClick={toggleRail}
                        aria-label={railCollapsed ? t('shell.rail.expand') : t('shell.rail.collapse')}
                        aria-expanded={!railCollapsed}
                      />
                    </Tooltip>
                  </div>
                  {!railCollapsed ? (
                    <div className={styles.railContent}>{leftRail}</div>
                  ) : null}
                </aside>
              ) : null}

              <div className={styles.bodyMain}>
                {tabs && tabs.length > 0 ? (
                  <>
                    <TabList
                      selectedValue={currentTab}
                      onTabSelect={(_, d) => setCurrentTab(d.value as string)}
                      size="small"
                      className={styles.tabList}
                    >
                      {tabs.map((tab) => (
                        <Tab key={tab.value} value={tab.value} icon={tab.icon}>
                          {tab.label}
                        </Tab>
                      ))}
                    </TabList>
                    <div className={mergeClasses(
                      styles.tabBody,
                      contentMode === 'fullBleed' && styles.tabBodyFullBleed,
                    )}>
                      <div className={mergeClasses(
                        styles.tabContent,
                        contentMode === 'fullBleed' && styles.tabContentFullBleed,
                      )}>
                        {tabs.find((tab) => tab.value === currentTab)?.render() ?? null}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className={mergeClasses(
                    styles.fallbackBody,
                    contentMode === 'fullBleed' && styles.fallbackBodyFullBleed,
                  )}>{children}</div>
                )}
              </div>
            </div>

            {detailDrawer ? (
              <DetailDrawer
                open={drawerOpen}
                onDismiss={closeDrawer}
                title={drawerItem ? detailDrawer.renderTitle(drawerItem) : ''}
                subtitle={
                  drawerItem && detailDrawer.renderSubtitle
                    ? detailDrawer.renderSubtitle(drawerItem)
                    : undefined
                }
                position={detailDrawer.position ?? 'end'}
                variant={detailDrawer.variant ?? 'inline'}
                width={detailDrawer.width ?? 360}
                size={detailDrawer.size ?? 'medium'}
                footer={
                  drawerItem && detailDrawer.renderFooter
                    ? detailDrawer.renderFooter(drawerItem)
                    : undefined
                }
              >
                {drawerItem ? detailDrawer.render(drawerItem) : null}
              </DetailDrawer>
            ) : null}
            </div>
          </AppLayout>
        </AppShellErrorBoundary>
      </DetailDrawerContext.Provider>
    </DensityProvider>
  );
}
